import { Color, Module, Space, Text } from '@billy/design-system'
import { differenceInDays, format } from 'date-fns'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useZervantSubscriptionDetails } from '@features/zervant-subscription/hooks/useZervantSubscriptionDetails'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { useOrganizationMarket, useOrganizationSubscription } from '../../../../../../hooks'
import { useOrganizationUiSetup } from '../../../../../../hooks/useOrganizationUiSetup'
import { plansWithNoPeriodSelection } from '../../plansData'
import { resolveSubscriptionPlan } from '../../utils/resolveSubscriptionPlan'
import { getPlanPriceSuffixTKey } from '../SubscriptionPlans/utils/getPlanPriceSuffixTKey'
import { SummaryBoxesComponent } from '../SummaryBoxesComponent'
import { SubscriptionPeriodToggle } from './elements/SubscriptionPeriodToggle'
import { SwitchPlanSectionMessageComponent } from './elements/SwitchPlanSectionMessageComponent'
import * as Styled from './styles'
import { getPeriodText } from './utils/getPeriodText'

export interface SectionPlanDetailsProps {
  isTrial: boolean
  switchPlan?: SubscriptionPlan
  isSwitchingPlan: boolean
  switchPlanPeriod?: SubscriptionPeriod
  planExpirationDate?: string | null
  subscriptionPeriod: SubscriptionPeriod | null
  subscriptionPlan: SubscriptionPlan
  onSubscriptionPeriodToggle: () => void
  isInUpdatePeriodState?: boolean
  hasDiscount: boolean
}

export const SectionPlanDetailsComponent = ({
  isTrial,
  switchPlan,
  isSwitchingPlan,
  switchPlanPeriod,
  planExpirationDate,
  subscriptionPeriod,
  subscriptionPlan, // ⚠️ this is the Organization.subscriptionPlan and not the Organization.subscriptionProductPlan
  onSubscriptionPeriodToggle,
  isInUpdatePeriodState,
  hasDiscount,
}: SectionPlanDetailsProps) => {
  const { t } = useTranslation()
  const period: SubscriptionPeriod = subscriptionPeriod || SubscriptionPeriod.Yearly
  const { subscriptionPricePerMonth } = useOrganizationSubscription()
  const { market } = useOrganizationMarket()
  const { organization } = useUserOrganization()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const { isInZervantNoPlan } = useZervantSubscriptionDetails()
  const isShineWithFreeZervantPlan = isUiSetupShineInvoicingAccounting && isInZervantNoPlan

  const subscriptionExpiringDate = format(new Date(planExpirationDate || new Date()), 'yyyy-MM-dd')
  const trialDays = subscriptionExpiringDate
    ? differenceInDays(new Date(subscriptionExpiringDate), new Date()) + 1
    : null

  const summaryItems = React.useMemo(() => {
    const resolvedPlan =
      organization && resolveSubscriptionPlan(organization.subscriptionProductPlan, market || undefined)
    const planNameText = t(resolvedPlan?.content.name ?? '')
    const periodText = organization && getPeriodText(organization.subscriptionProductPlan, period, t)

    const planName =
      isTrial && trialDays ? (
        <>
          <Text as="span" weight="medium" inline>
            {`${planNameText} - `}
          </Text>
          <Text as="span" weight="medium" color={Color.Yellow700} inline>
            {t('organization_subscription.days_of_free_trial_left', { days: trialDays })}
          </Text>
        </>
      ) : (
        `${planNameText}, ${periodText}`
      )
    const items = [
      {
        name: t('organization_subscription.summary_current_plan'),
        value: planName,
      },
      {
        name: t('organization_subscription.cost'),
        value: `${isTrial || isShineWithFreeZervantPlan ? 0 : subscriptionPricePerMonth} ${t(getPlanPriceSuffixTKey(market))}`,
      },
    ]

    if (
      subscriptionPeriod &&
      !plansWithNoPeriodSelection.includes(subscriptionPlan) &&
      !hasDiscount &&
      !isSwitchingPlan &&
      !isTrial &&
      !isShineWithFreeZervantPlan
    ) {
      items.push({
        name: t('organization_subscription.billing_type'),
        value: (
          <SubscriptionPeriodToggle
            subscriptionPeriod={subscriptionPeriod}
            isInUpdatePeriodState={isInUpdatePeriodState}
            onToggle={onSubscriptionPeriodToggle}
            isDisabled={subscriptionPlan === SubscriptionPlan.Archive}
          />
        ),
      })
    }

    return items
  }, [
    subscriptionPricePerMonth,
    hasDiscount,
    isInUpdatePeriodState,
    isShineWithFreeZervantPlan,
    isSwitchingPlan,
    isTrial,
    onSubscriptionPeriodToggle,
    period,
    subscriptionPeriod,
    subscriptionPlan,
    t,
    trialDays,
    market,
    organization,
  ])

  const shouldDisplayRenewalDate = useMemo(() => {
    return (
      !isShineWithFreeZervantPlan &&
      !isSwitchingPlan &&
      subscriptionExpiringDate &&
      !plansWithNoPeriodSelection.includes(subscriptionPlan)
    )
  }, [isShineWithFreeZervantPlan, isSwitchingPlan, subscriptionExpiringDate, subscriptionPlan])

  return (
    <Module.Section title={t('organization_subscription.plan_details')}>
      <SummaryBoxesComponent items={summaryItems} />
      {isSwitchingPlan && switchPlan && (
        <Styled.SectionMessageContainer>
          <SwitchPlanSectionMessageComponent
            currentPlan={subscriptionPlan}
            market={market}
            switchPlan={switchPlan}
            switchPlanPeriod={switchPlanPeriod}
            subscriptionExpiringDate={subscriptionExpiringDate}
          />
        </Styled.SectionMessageContainer>
      )}
      {shouldDisplayRenewalDate && (
        <>
          <Space size="s16" />
          <Text variant="bodySM" colorVariant="secondary">
            {t('organization_subscription.renewal_on')}
            <strong>{subscriptionExpiringDate}</strong>.
          </Text>
        </>
      )}
    </Module.Section>
  )
}
